<script setup>
import { computed, onMounted, ref } from 'vue'

import Button from 'primevue/button'
import Card from 'primevue/card'
import Checkbox from 'primevue/checkbox'
import Dropdown from 'primevue/dropdown'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'

import { useAppToast } from '@/shared/lib/useAppToast'
import {
  createProductVariant,
  deleteProductVariant,
  getProductVariants,
  updateProductVariant
} from '@/modules/admin/products/products/api/products.service'

const props = defineProps({
  productId: {
    type: Number,
    required: true
  }
})

const toast = useAppToast()

const variants = ref([])
const loading = ref(false)
const submitting = ref(false)
const deleting = ref(false)
const editingId = ref(null)
const formVisible = ref(false)

const emptyForm = {
  variantArticul: '',
  finishCode: '',
  finishName: '',
  finishGroup: '',
  price: 0,
  currency: 'EUR',
  quantity: 0,
  isDefault: false,
  isActive: true
}

const form = ref({ ...emptyForm })

const currencyOptions = [
  { label: 'EUR', value: 'EUR' },
  { label: 'KZT', value: 'KZT' },
  { label: 'USD', value: 'USD' }
]

const isEditing = computed(() => editingId.value !== null)

function normalizeResponse(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.value)) return data.value
  return []
}

async function loadVariants() {
  try {
    loading.value = true
    const data = await getProductVariants(props.productId)
    variants.value = normalizeResponse(data)
  } catch (error) {
    variants.value = []
    toast.error(error.message || 'Ошибка загрузки вариантов товара')
  } finally {
    loading.value = false
  }
}

function resetForm() {
  editingId.value = null
  form.value = { ...emptyForm }
  formVisible.value = false
}

function openCreateForm() {
  editingId.value = null
  form.value = { ...emptyForm }
  formVisible.value = true
}

function editVariant(variant) {
  editingId.value = variant.id
  form.value = {
    variantArticul: variant.variantArticul ?? '',
    finishCode: variant.finishCode ?? '',
    finishName: variant.finishName ?? '',
    finishGroup: variant.finishGroup ?? '',
    price: Number(variant.price ?? 0),
    currency: variant.currency ?? 'EUR',
    quantity: Number(variant.quantity ?? 0),
    isDefault: !!variant.isDefault,
    isActive: !!variant.isActive
  }
    formVisible.value = true
}

function buildPayload() {
  return {
    productId: props.productId,
    variantArticul: form.value.variantArticul?.trim(),
    finishCode: form.value.finishCode?.trim() || null,
    finishName: form.value.finishName?.trim() || null,
    finishGroup: form.value.finishGroup || null,
    price: Number(form.value.price ?? 0),
    currency: form.value.currency || 'EUR',
    quantity: Number(form.value.quantity ?? 0),
    isDefault: !!form.value.isDefault,
    isActive: !!form.value.isActive
  }
}

async function saveVariant() {
  const payload = buildPayload()

  if (!payload.variantArticul) {
    toast.error('Укажите артикул варианта')
    return
  }

  if (!payload.finishName) {
    toast.error('Укажите название отделки')
    return
  }

  try {
    submitting.value = true

    if (isEditing.value) {
      await updateProductVariant(props.productId, editingId.value, {
        id: editingId.value,
        ...payload
      })
      toast.success('Вариант обновлён')
    } else {
      await createProductVariant(props.productId, payload)
      toast.success('Вариант создан')
    }

    resetForm()
    await loadVariants()
  } catch (error) {
    toast.error(error.message || `Ошибка сохранения варианта${error.status ? ` (${error.status})` : ''}`)
    console.error('Save product variant error:', error)
  } finally {
    submitting.value = false
  }
}

async function removeVariant(variant) {
  if (deleting.value) return
  if (!confirm(`Удалить вариант ${variant.variantArticul}?`)) return

  deleting.value = true
  try {
    await deleteProductVariant(props.productId, variant.id)
    toast.success('Вариант удалён')
    await loadVariants()
  } catch (error) {
    toast.error(error.message || `Ошибка удаления варианта${error.status ? ` (${error.status})` : ''}`)
    console.error('Delete product variant error:', error)
  } finally {
    deleting.value = false
  }
}

onMounted(loadVariants)
</script>

<template>
  <Card class="variants-card">
    <template #title>Варианты товара / отделки</template>

    <template #content>
      <div v-if="loading" class="state-block">Загрузка вариантов...</div>

      <div v-else-if="!variants.length" class="state-block">
        Варианты ещё не добавлены
      </div>

      <div v-else class="variants-list">
        <div
          v-for="variant in variants"
          :key="variant.id"
          class="variant-row"
        >
          <div class="variant-info">
            <div class="variant-title">
              {{ variant.variantArticul }}
              <span v-if="variant.isDefault" class="badge">Default</span>
              <span v-if="!variant.isActive" class="badge muted">Inactive</span>
            </div>

            <div class="variant-meta">
              <span>{{ variant.finishGroup }}</span>
              <span>{{ variant.finishName }}</span>
              <span>{{ variant.price }} {{ variant.currency }}</span>
              <span>Остаток: {{ variant.quantity }}</span>
            </div>
          </div>

          <div class="variant-actions">
            <Button
              icon="pi pi-pencil"
              severity="secondary"
              outlined
              @click="editVariant(variant)"
            />
            <Button
              icon="pi pi-trash"
              severity="danger"
              outlined
              :disabled="deleting"
              @click="removeVariant(variant)"
            />
          </div>
        </div>
      </div>

            <div v-if="!formVisible" class="form-actions">
        <Button
          label="Добавить вариант"
          icon="pi pi-plus"
          @click="openCreateForm"
        />
      </div>

      <template v-if="formVisible">
      <div class="form-grid">
        <div class="field">
          <label>Артикул варианта</label>
          <InputText v-model="form.variantArticul" placeholder="RV605CR" />
        </div>

        <div class="field">
          <label>Группа варианта, необязательно</label>
          <InputText v-model="form.finishGroup" placeholder="Fin A / Цвет / Размер" />
        </div>

        <div class="field">
          <label>Код отделки</label>
          <InputText v-model="form.finishCode" placeholder="CR / 14 / DP" />
        </div>

        <div class="field">
          <label>Название отделки</label>
          <InputText v-model="form.finishName" placeholder="Chrome" />
        </div>

        <div class="field">
          <label>Цена</label>
          <InputNumber v-model="form.price" :min="0" :minFractionDigits="2" />
        </div>

        <div class="field">
          <label>Валюта</label>
          <Dropdown
            v-model="form.currency"
            :options="currencyOptions"
            optionLabel="label"
            optionValue="value"
          />
        </div>

        <div class="field">
          <label>Количество</label>
          <InputNumber v-model="form.quantity" :min="0" />
        </div>

        <div class="checks">
          <label>
            <Checkbox v-model="form.isDefault" binary />
            По умолчанию
          </label>

          <label>
            <Checkbox v-model="form.isActive" binary />
            Активен
          </label>
        </div>
      </div>

      <div class="form-actions">
        <Button
          :label="isEditing ? 'Сохранить вариант' : 'Добавить вариант'"
          icon="pi pi-check"
          :loading="submitting"
          @click="saveVariant"
        />

        <Button
          label="Отмена"
          severity="secondary"
          outlined
          @click="resetForm"
        />
      </div>
      </template>

    </template>
  </Card>
</template>

<style scoped>
.variants-card {
  margin-top: 24px;
}

.state-block {
  padding: 20px;
  text-align: center;
  color: #6b7280;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
}

.variants-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
}

.variant-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.variant-title {
  font-weight: 700;
  display: flex;
  gap: 8px;
  align-items: center;
}

.variant-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
  color: #6b7280;
  font-size: 14px;
}

.variant-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.badge {
  font-size: 11px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #111111;
  color: #ffffff;
}

.badge.muted {
  background: #9ca3af;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label,
.checks label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.checks {
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: end;
}

.checks label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

:deep(.p-inputtext),
:deep(.p-dropdown),
:deep(.p-inputnumber),
:deep(.p-inputnumber-input) {
  width: 100%;
}

@media (max-width: 900px) {
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }

  .variant-row {
    flex-direction: column;
  }
}

@media (max-width: 640px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
